import { useCallback, useState } from 'react';
import {
  Box,
  Button,
  DescriptionList,
  Flex,
  GridAlpha,
  Heading,
  Link,
  Spinner,
} from '@applyboard/crystal-ui';
import { DeleteFillIcon } from '@applyboard/ui-icons';
import {
  DefinedUser,
  useCurrentUser,
  useEventTracker,
  useFeatureDecision,
} from '@applyboard/ui-utils';
import { appendQueryParameters } from '../../common/utils/appendQueryParameters';
import { useArchiveNotification } from '../../data/notifications/notification';
import {
  NotificationTemplate,
  useGetNotificationTemplates,
} from '../../data/notifications/notificationTemplates';
import { usePauseNudgeRule } from '../../data/notifications/nudgeRules';
import { Notification } from './types';

const NOTIFICATION_TRACKING_SOURCE = 'vas_hub_notification';

type NotificationContentProps = {
  notifications: Notification[];
  onChange?: () => void;
};

const DismissNotificationIcon = () => <DeleteFillIcon intent="primary" size="md" />;

function generateNotificationMessages(
  notifications: Notification[],
  templateMap: Record<string, NotificationTemplate>,
): {
  id: string;
  rule_name: string;
  message_title: string;
  message: string;
  action_url: string;
  action_label: string;
}[] {
  const filteredNotifications = notifications.filter(notification => {
    const template = templateMap[notification.rule_name];

    if (!template) {
      console.error('Failed to find notification template for rule name:', notification.rule_name);
    }

    return !!template;
  });

  // Final generated notification message with the notification id
  return filteredNotifications
    .map(notication => {
      const template = templateMap[notication.rule_name];
      return {
        id: notication.id,
        ...template,
        created_at: notication.created_at,
      };
    })
    .sort((notif1, notif2) => Date.parse(notif1.created_at) - Date.parse(notif2.created_at));
}

const NotificationContent = ({ notifications, onChange }: NotificationContentProps) => {
  const [currentUser] = useCurrentUser();
  const [decisionEngine, decisionEngineReady] = useFeatureDecision();

  const isNepalShopifyStoreEnabled =
    decisionEngine?.isFeatureEnabled('vas_nepal_shoppify_store') ?? false;

  const { notificationTemplateMap } = useGetNotificationTemplates(
    currentUser ?? ({} as DefinedUser),
    isNepalShopifyStoreEnabled,
  );
  const rpId = currentUser?.id ?? '';
  const { trackEvent } = useEventTracker();
  const { archiveNotificationAsync } = useArchiveNotification();
  const { pauseNudgeRuleAsync } = usePauseNudgeRule();
  const [archiveButtonDisabledState, setArchiveButtonDisabledState] = useState(
    {} as Record<string, boolean>,
  );

  const dismissAndPauseNotification = useCallback(
    async (notificationId: string, ruleName: string) => {
      await Promise.all([archiveNotificationAsync(notificationId), pauseNudgeRuleAsync(ruleName)]);
      onChange?.();
    },
    [archiveNotificationAsync, pauseNudgeRuleAsync, onChange],
  );

  const archive = useCallback(
    async (notificationId: string, ruleName: string) => {
      setArchiveButtonDisabledState(prevState => ({
        ...prevState,
        [notificationId]: true,
      }));

      await dismissAndPauseNotification(notificationId, ruleName);

      trackEvent({
        event: 'VAS - Vas Hub Page - Nudge archived',
        data: {
          service: notificationId,
          rule_name: ruleName,
        },
      });

      setArchiveButtonDisabledState(prevState => {
        delete prevState[notificationId];
        return prevState;
      });
    },
    [dismissAndPauseNotification, trackEvent],
  );

  const generatedContent = generateNotificationMessages(notifications, notificationTemplateMap);
  if (!decisionEngineReady) {
    return (
      <Flex align="center" justify="center">
        <Spinner size="xl" />
      </Flex>
    );
  }
  return (
    <>
      <DescriptionList variant="free">
        <GridAlpha columns="auto 1fr" columnGap={4} rowGap={4}>
          {generatedContent.length === 0 && (
            <>
              <Heading level={3} intent="positive">
                No messages
              </Heading>
            </>
          )}
          {generatedContent.map(notification => (
            <>
              <DescriptionList.Term>
                <Button
                  aria-label="dismiss-notification"
                  emphasis="transparent"
                  intent="primary"
                  onClick={() => archive(notification.id, notification.rule_name)}
                  size="sm"
                  type="button"
                  width="hug"
                  disabled={archiveButtonDisabledState[notification.id] === true}
                  leadIcon={DismissNotificationIcon}
                />
              </DescriptionList.Term>
              <Flex justify="start" align="center">
                <DescriptionList.Details>
                  <Flex direction="column" gap={2}>
                    <Heading level={3} intent="positive" variant="titleM">
                      {notification.message_title}
                    </Heading>
                    <Box>
                      <Heading level={4} intent="positive" variant="bodyS">
                        {notification.message}{' '}
                        {notification.action_url && (
                          <Link
                            href={appendQueryParameters(notification.action_url, {
                              rp_id: rpId,
                              source: NOTIFICATION_TRACKING_SOURCE,
                            })}
                            onClick={async () => {
                              await archiveNotificationAsync(notification.id);
                              trackEvent({
                                event: 'VAS - Vas Hub Page - Nudge CTA clicked',
                                data: {
                                  service: notification.id,
                                },
                              });
                            }}
                            variant="inline"
                            mode="override_user_choice_and_open_in_new_tab"
                          >
                            {notification.action_label}
                          </Link>
                        )}
                        {!notification.action_url && (
                          <Button
                            disabled
                            aria-label={`${notification.action_label}-buy-now-button`}
                            emphasis="transparent"
                            intent="positive"
                            size="sm"
                            width="hug"
                          >
                            {notification.action_label}
                          </Button>
                        )}
                      </Heading>
                    </Box>
                  </Flex>
                </DescriptionList.Details>
              </Flex>
            </>
          ))}
        </GridAlpha>
      </DescriptionList>
    </>
  );
};

export default NotificationContent;
